import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { uploadFile, type UploadKind, type UploadResult } from '../api/uploads.api';

interface UploadArgs {
  kind: UploadKind;
  file: File;
}

/**
 * Presign-then-PUT upload mutation (MYN-46 / FR-19, FR-20). Wraps the two-step
 * S3 presigned-URL flow so components get a single mutate call plus loading,
 * error and progress state; callers persist the returned fileKey into the form.
 *
 * `progress` is `undefined` rather than 0 when the transfer reports no total
 * (a proxy stripping `content-length`), so a caller can tell "0% done" apart
 * from "no percentage available" and fall back to an indeterminate indicator.
 *
 * The reset lives inside `mutationFn` rather than in a wrapper around `mutate`,
 * so `mutateAsync` gets it too - a wrapper would have left that path showing
 * the previous upload's completed bar.
 */
export function useUploadFile() {
  const [progress, setProgress] = useState<number | undefined>(undefined);

  const mutation = useMutation<UploadResult, Error, UploadArgs>({
    mutationFn: ({ kind, file }) => {
      setProgress(undefined);
      return uploadFile(kind, file, setProgress);
    },
  });

  return { ...mutation, progress };
}
